import { z } from 'zod'
import { asObjectType, asEnumType, asField } from '@gqloom/zod'
import { GraphQLJSON } from 'graphql-scalars'
import { formRequiredBeforeSchema } from './form-workflow-schemas'
import { int32Schema, scoreBandSchema } from './score-band-schemas'

export const SiteFormQuestionType = {
  Text: 'text',
  Select: 'select',
  Boolean: 'boolean',
  Radio: 'radio',
  Range: 'range',
  Signature: 'signature',
  TextBlurb: 'textBlurb',
} as const

export type SiteFormQuestionType = (typeof SiteFormQuestionType)[keyof typeof SiteFormQuestionType]

export const LEGACY_EMPTY_SITE_FORM_DESCRIPTION = 'No description provided'

export const toMemberFormDescription = (description: string | null | undefined): string | null =>
  description && description !== LEGACY_EMPTY_SITE_FORM_DESCRIPTION ? description : null

// Register enum with proper name for GraphQL
export const siteFormQuestionTypeSchema = z
  .enum(Object.values(SiteFormQuestionType) as [string, ...string[]])
  .register(asEnumType, {
    name: 'SiteFormQuestionType',
  })

// Site form question schema - handles database transformation
export const siteFormQuestionSchema = z
  .object({
    siteFormQuestionId: z.string(),
    siteFormId: z.string(),
    title: z.string().min(1).max(255),
    orderIndex: z.number().int().positive(),
    type: siteFormQuestionTypeSchema,
    options: z.array(z.string()).nullable().optional(),
    siteCustomPropertyId: z.string().nullable().optional(),
    optionScores: z.array(int32Schema).nullable().optional(),
    excludeFromScore: z.boolean().nullable().optional(),
    required: z.boolean().nullable().optional(),
    mustAnswerYes: z.boolean().nullable().optional(),
    createdAt: z.date().nullable().optional(),
    updatedAt: z.date().nullable().optional(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SiteFormQuestion',
    description: 'A question within a site form',
  })

// Site form question input schemas
export const siteFormQuestionCreateInputSchema = z
  .object({
    title: z
      .string()
      .min(1, 'Title is required')
      .max(255, 'Title must be less than 255 characters'),
    type: siteFormQuestionTypeSchema,
    options: z.array(z.string()).optional(),
    siteCustomPropertyId: z.uuid().nullable().optional(),
  })
  .refine(
    (data) => {
      // SELECT and RADIO types require at least 2 options
      if (data.type === SiteFormQuestionType.Select || data.type === SiteFormQuestionType.Radio) {
        return data.options && data.options.length >= 2
      }
      return true
    },
    {
      message: 'Select/Radio question must have at least two options',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // RANGE type requires exactly 4 options [min, max, step, unit]
      if (data.type === SiteFormQuestionType.Range) {
        return data.options && data.options.length === 4
      }
      return true
    },
    {
      message: 'Range question must have exactly 4 options: [min, max, step, unit]',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT, BOOLEAN, and SIGNATURE types cannot have options
      if (
        data.type === SiteFormQuestionType.Text ||
        data.type === SiteFormQuestionType.Boolean ||
        data.type === SiteFormQuestionType.Signature
      ) {
        return !data.options || data.options.length === 0
      }
      return true
    },
    {
      message: 'Text/Boolean/Signature question cannot have options',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT_BLURB type requires exactly 1 option (the content text)
      if (data.type === SiteFormQuestionType.TextBlurb) {
        return data.options && data.options.length === 1
      }
      return true
    },
    {
      message: 'TextBlurb question must have exactly 1 option: [content]',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT_BLURB content must not exceed 2000 characters
      if (data.type === SiteFormQuestionType.TextBlurb && data.options) {
        return data.options[0] && data.options[0].length <= 2000
      }
      return true
    },
    {
      message: 'TextBlurb content must not exceed 2000 characters',
      path: ['options'],
    }
  )

export const siteFormQuestionUpdateInputSchema = z
  .object({
    title: z
      .string()
      .min(1, 'Title is required')
      .max(255, 'Title must be less than 255 characters')
      .optional(),
    type: siteFormQuestionTypeSchema.optional(),
    options: z.array(z.string()).optional(),
    siteCustomPropertyId: z.uuid().nullable().optional(),
  })
  .refine(
    (data) => {
      // SELECT and RADIO types require at least 2 options (only validate if type is being updated)
      if (
        (data.type === SiteFormQuestionType.Select || data.type === SiteFormQuestionType.Radio) &&
        data.options !== undefined
      ) {
        return data.options && data.options.length >= 2
      }
      return true
    },
    {
      message: 'Select/Radio question must have at least two options',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // RANGE type requires exactly 4 options (only validate if type is being updated to RANGE)
      if (data.type === SiteFormQuestionType.Range && data.options !== undefined) {
        return data.options && data.options.length === 4
      }
      return true
    },
    {
      message: 'Range question must have exactly 4 options: [min, max, step, unit]',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT, BOOLEAN, and SIGNATURE types cannot have options (only validate if type is being updated)
      if (
        (data.type === SiteFormQuestionType.Text ||
          data.type === SiteFormQuestionType.Boolean ||
          data.type === SiteFormQuestionType.Signature) &&
        data.options !== undefined
      ) {
        return !data.options || data.options.length === 0
      }
      return true
    },
    {
      message: 'Text/Boolean/Signature question cannot have options',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT_BLURB type requires exactly 1 option (only validate if type is being updated to TEXT_BLURB)
      if (data.type === SiteFormQuestionType.TextBlurb && data.options !== undefined) {
        return data.options && data.options.length === 1
      }
      return true
    },
    {
      message: 'TextBlurb question must have exactly 1 option: [content]',
      path: ['options'],
    }
  )
  .refine(
    (data) => {
      // TEXT_BLURB content must not exceed 2000 characters (only validate if updating)
      if (data.type === SiteFormQuestionType.TextBlurb && data.options && data.options.length > 0) {
        return data.options[0] && data.options[0].length <= 2000
      }
      return true
    },
    {
      message: 'TextBlurb content must not exceed 2000 characters',
      path: ['options'],
    }
  )

const siteFormQuestionReorderInputSchema = z.object({
  questionId: z.string(),
  newOrder: z.number().int().positive(),
})

const siteFormResultPropertyMappingFieldSchema = z
  .record(z.string(), z.string())
  .nullable()
  .optional()
  .register(asField, {
    type: GraphQLJSON,
    description: 'Band id to select-option text for a mapped select property; null otherwise',
  })

// Main site form schema - handles database transformation
export const siteFormSchema = z
  .object({
    siteFormId: z.string(),
    siteId: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().max(1000),
    questions: z.array(siteFormQuestionSchema),
    scoringEnabled: z.boolean().nullable().optional(),
    scoreBands: z.array(scoreBandSchema).nullable().optional(),
    revision: z.number().int().nullable().optional(),
    defaultRequiredBefore: formRequiredBeforeSchema.nullable().optional(),
    showResultOnProfile: z.boolean().nullable().optional(),
    showResultToClient: z.boolean().nullable().optional(),
    resultCustomPropertyId: z.string().nullable().optional(),
    resultPropertyMapping: siteFormResultPropertyMappingFieldSchema,
    templateKey: z.string().nullable().optional(),
    createdAt: z.date().nullable().optional(),
    updatedAt: z.date().nullable().optional(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'SiteForm',
    description: 'A form template for collecting data at a site',
  })

// Site form input schemas
export const siteFormCreateSchema = z
  .object({
    siteId: z.string(),
    name: z.string().min(1, 'Name is required').max(255, 'Name must be less than 255 characters'),
    description: z.string().max(1000, 'Description must be less than 1000 characters'),
  })
  .register(asObjectType, {
    name: 'SiteFormCreateInput',
    description: 'Input for creating site forms',
  })

export const siteFormUpdateSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(255, 'Name must be less than 255 characters')
      .optional(),
    description: z.string().max(1000, 'Description must be less than 1000 characters').optional(),
  })
  .register(asObjectType, {
    name: 'SiteFormUpdateInput',
    description: 'Input for updating site forms',
  })

// Question input schemas with GLoom registration
export const siteFormQuestionCreateInputSchemaGLoom = siteFormQuestionCreateInputSchema.register(
  asObjectType,
  {
    name: 'SiteFormQuestionCreateInput',
    description: 'Input for creating site form questions',
  }
)

export const siteFormQuestionUpdateInputSchemaGLoom = siteFormQuestionUpdateInputSchema.register(
  asObjectType,
  {
    name: 'SiteFormQuestionUpdateInput',
    description: 'Input for updating site form questions',
  }
)

export const siteFormQuestionReorderInputSchemaGLoom = siteFormQuestionReorderInputSchema.register(
  asObjectType,
  {
    name: 'SiteFormQuestionReorderInput',
    description: 'Input for reordering site form questions',
  }
)

// Response schemas for GraphQL mutations
export const siteFormQuestionOrderResponseSchema = z
  .object({
    orderIndex: z.number(),
  })
  .register(asObjectType, {
    name: 'SiteFormQuestionOrderResponse',
    description: 'Response containing the order index of a question',
  })

export const siteFormMutationResponseSchema = z
  .object({
    updated: z.boolean(),
  })
  .register(asObjectType, {
    name: 'SiteFormMutationResponse',
    description: 'Response for site form mutation operations',
  })

export const siteFormDeleteResponseSchema = z
  .object({
    deleted: z.boolean(),
  })
  .register(asObjectType, {
    name: 'SiteFormDeleteResponse',
    description: 'Response for site form delete operations',
  })

export const siteFormReorderResponseSchema = z
  .object({
    updatedCount: z.number(),
  })
  .register(asObjectType, {
    name: 'SiteFormReorderResponse',
    description: 'Response containing the count of updated questions',
  })

// Type exports
export type SiteFormSchema = z.infer<typeof siteFormSchema>
export type SiteFormCreateSchema = z.infer<typeof siteFormCreateSchema>
export type SiteFormUpdateSchema = z.infer<typeof siteFormUpdateSchema>
export type SiteFormQuestion = z.infer<typeof siteFormQuestionSchema>
export type SiteFormQuestionCreateInput = z.infer<typeof siteFormQuestionCreateInputSchema>
export type SiteFormQuestionUpdateInput = z.infer<typeof siteFormQuestionUpdateInputSchema>
export type SiteFormQuestionReorderInput = z.infer<typeof siteFormQuestionReorderInputSchema>

export const siteFormTemplateSchema = z
  .object({
    key: z.string(),
    name: z.string(),
    description: z.string(),
    questionCount: z.number().int(),
    bandCount: z.number().int(),
    source: z.string(),
  })
  .register(asObjectType, {
    name: 'SiteFormTemplate',
    description: 'A Hapana-provided form a location can create an editable copy of',
  })

export const saveScoredSiteFormResultSchema = z
  .object({
    form: siteFormSchema,
    revision: z.number().int(),
    reissueCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'SaveScoredSiteFormResult',
    description:
      'The saved form, its new revision and how many unscored pending assignments were withdrawn',
  })

export const scoringConversionPreviewSchema = z
  .object({
    siteFormId: z.string(),
    pendingAssignmentCount: z.number().int(),
    completedSubmissionCount: z.number().int(),
  })
  .register(asObjectType, {
    name: 'ScoringConversionPreview',
    description:
      'Pending unscored assignments that turning on scoring would withdraw, and completed submissions that keep their past scoring',
  })

export type SiteFormTemplateView = z.infer<typeof siteFormTemplateSchema>
export type SaveScoredSiteFormResult = z.infer<typeof saveScoredSiteFormResultSchema>
export type ScoringConversionPreview = z.infer<typeof scoringConversionPreviewSchema>
